import { useEffect, useRef, useState } from "react";

import {
  SynchronizedClock,
  type ClockStatus,
} from "../features/countdown";

export interface SynchronizedClockView {
  readonly nowEpochMs: number;
  readonly status: ClockStatus;
  readonly error: string | null;
}

export function useSynchronizedClock(): SynchronizedClockView {
  const clockRef = useRef<SynchronizedClock | null>(null);
  if (clockRef.current === null) {
    clockRef.current = new SynchronizedClock();
  }

  const clock = clockRef.current;
  const [nowEpochMs, setNowEpochMs] = useState(() => clock.nowEpochMs());
  const [status, setStatus] = useState(() => clock.getStatus());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const synchronize = async (force = false): Promise<void> => {
      try {
        if (force) {
          await clock.resyncAfterResume({ sampleCount: 3 });
        } else {
          await clock.resyncIfDue({ sampleCount: 3 });
        }
        if (!active) return;
        setStatus(clock.getStatus());
        setError(null);
        setNowEpochMs(clock.nowEpochMs());
      } catch {
        if (!active) return;
        setStatus(clock.getStatus());
        setError("基準時刻を取得できません。自動で再同期しています。");
      }
    };

    void synchronize();

    const tickId = window.setInterval(() => {
      setNowEpochMs(clock.nowEpochMs());
    }, 200);
    const resyncId = window.setInterval(() => {
      void synchronize();
    }, 10_000);

    const handleResume = (): void => {
      if (document.visibilityState === "visible") {
        setNowEpochMs(clock.nowEpochMs());
        void synchronize(true);
      }
    };

    const handleOnline = (): void => {
      void synchronize(true);
    };

    const handleFocus = (): void => {
      if (document.visibilityState === "visible") void synchronize(true);
    };

    document.addEventListener("visibilitychange", handleResume);
    window.addEventListener("pageshow", handleResume);
    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleFocus);

    return () => {
      active = false;
      window.clearInterval(tickId);
      window.clearInterval(resyncId);
      document.removeEventListener("visibilitychange", handleResume);
      window.removeEventListener("pageshow", handleResume);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleFocus);
    };
  }, [clock]);

  return { nowEpochMs, status, error };
}
